import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  Braces,
  FileImage,
  FileText,
  Gauge,
  KeyRound,
  Link,
  NotebookText,
  RotateCw,
  Settings,
  Type,
} from 'lucide-react';
import { SeoMetricCard } from './components/SeoMetricCard';
import { IssueSummaryChip } from './components/IssueSummaryChip';
import { OptimizationTask } from './components/OptimizationTask';
import type {
  SeoMetricStatus,
  SeoTaskPriority,
} from 'features/DigitalMarketing/constants/seo';
import { ReactComponent as SeoGaugeAsset } from 'assets/images/DigitalMarketing/seo-gauge.svg';
import { ReactComponent as SeoGaugeSkeletonAsset } from 'assets/images/DigitalMarketing/seo-gauge-skeleton.svg';
import {
  SeoOptimizationItem,
  SeoPriority,
  SeoReport,
} from 'features/DigitalMarketing/api/seoApi';
import { useSeoHealth } from 'features/DigitalMarketing/hooks/useSeoHealth';
import { DMButton, DMDiv, DMText, DMAnchor } from '../../components/Primitives';

const metricMeta = {
  content_quality: {
    label: 'Content Quality',
    icon: FileText,
    iconClassName: 'text-indigo-600',
    iconBgClassName: 'bg-indigo-100',
  },
  backlink_signals: {
    label: 'Back Link Signals',
    icon: Link,
    iconClassName: 'text-blue-600',
    iconBgClassName: 'bg-blue-50',
  },
  ux_signals: {
    label: 'UX Signals',
    icon: Gauge,
    iconClassName: 'text-violet-600',
    iconBgClassName: 'bg-violet-50',
  },
  schema_markup: {
    label: 'Schema Markup',
    icon: Braces,
    iconClassName: 'text-orange-600',
    iconBgClassName: 'bg-orange-50',
  },
  technical_seo: {
    label: 'Technical SEO',
    icon: Settings,
    iconClassName: 'text-emerald-600',
    iconBgClassName: 'bg-emerald-50',
  },
  keyword_analysis: {
    label: 'Key Word Analysis',
    icon: KeyRound,
    iconClassName: 'text-indigo-600',
    iconBgClassName: 'bg-indigo-50',
  },
  on_page_seo: {
    label: 'On - page SEO',
    icon: NotebookText,
    iconClassName: 'text-sky-600',
    iconBgClassName: 'bg-sky-50',
  },
  local_seo: {
    label: 'Local SEO',
    icon: NotebookText,
    iconClassName: 'text-sky-600',
    iconBgClassName: 'bg-sky-50',
  },
  reputation_trust: {
    label: 'Reputation Trust',
    icon: Gauge,
    iconClassName: 'text-emerald-600',
    iconBgClassName: 'bg-emerald-50',
  },
};

const leftMetricKeys = [
  'content_quality',
  'backlink_signals',
  'ux_signals',
  'schema_markup',
] as const;
const rightMetricKeys = [
  'technical_seo',
  'keyword_analysis',
  'on_page_seo',
  'local_seo',
  'reputation_trust',
] as const;

const skeletonBaseClassName = 'rounded-[0.25vh] bg-gray-300/80';
const skeletonSoftClassName = 'rounded-[0.25vh] bg-gray-200/80';

const SkeletonBlock = ({ className }: { className: string }) => (
  <DMDiv
    className={`${skeletonBaseClassName} ${className}`}
    aria-hidden="true"
  />
);

const SkeletonSoftBlock = ({ className }: { className: string }) => (
  <DMDiv
    className={`${skeletonSoftClassName} ${className}`}
    aria-hidden="true"
  />
);

const SeoMetricSkeletonCard = () => (
  <DMDiv className="flex items-center justify-between rounded-[0.35vh] border border-gray-100 bg-gray-50/80 px-[1vh] py-[1vh]">
    <DMDiv className="flex min-w-0 items-center gap-[1.35vh]">
      <SkeletonSoftBlock className="h-[3.3vh] w-[3.3vh]" />
      <DMDiv>
        <SkeletonBlock className="h-[1.35vh] w-[13vh]" />
        <SkeletonSoftBlock className="mt-[1vh] h-[1.15vh] w-[10.5vh]" />
      </DMDiv>
    </DMDiv>
    <DMDiv className="flex flex-col items-end gap-[0.95vh]">
      <SkeletonSoftBlock className="h-[1.7vh] w-[4.8vh] rounded-full" />
      <SkeletonBlock className="h-[1.35vh] w-[3.8vh]" />
    </DMDiv>
  </DMDiv>
);

const SeoScoreSkeletonPanel = () => (
  <DMDiv
    as="section"
    className="flex h-full min-h-0 flex-col items-center rounded-[0.2vh] border border-gray-200 bg-white/80 px-[3vh] py-[1.25vh] text-center"
  >
    <SkeletonBlock className="h-[1.15vh] w-[16.5vh]" />
    <SkeletonSoftBlock className="mt-[1.1vh] h-[1.2vh] w-[17.2vh]" />

    <DMDiv className="relative mt-[1vh] h-[13.5vh] w-[23vh]">
      <SeoGaugeSkeletonAsset
        className="h-full w-full overflow-visible"
        aria-hidden="true"
      />
      <SkeletonSoftBlock className="absolute left-1/2 top-[5.45vh] h-[4.65vh] w-[4.65vh] -translate-x-1/2" />
    </DMDiv>

    <SkeletonBlock className="mt-[0.65vh] h-[1.7vh] w-[13.8vh]" />
    <SkeletonSoftBlock className="mt-[1.3vh] h-[4.35vh] w-[15.4vh] rounded-[0.5vh]" />
    <SkeletonSoftBlock className="mt-[1.35vh] h-[3.3vh] w-[12.5vh]" />
    <DMDiv className="mt-[1.05vh] flex items-center justify-center gap-[3vh]">
      <SkeletonSoftBlock className="h-[3.2vh] w-[5.6vh]" />
      <SkeletonSoftBlock className="h-[3.2vh] w-[5.6vh]" />
      <SkeletonSoftBlock className="h-[3.2vh] w-[5.6vh]" />
    </DMDiv>
  </DMDiv>
);

const OptimizationPlanSkeleton = () => (
  <DMDiv
    as="section"
    className="mt-[2.4vh] shrink-0 overflow-hidden rounded-[1.5vh] border border-gray-100 bg-white/80 shadow-[0_1px_10px_rgba(15,23,42,0.04)]"
  >
    <DMDiv className="px-[3vh] py-[1.8vh]">
      <SkeletonBlock className="h-[3.55vh] w-[32vh] max-w-full" />
      <SkeletonSoftBlock className="mt-[1vh] h-[2vh] w-[58vh] max-w-full" />
    </DMDiv>

    <DMDiv>
      {[0, 1].map(item => (
        <DMDiv
          key={item}
          className="flex items-center justify-between border-t border-gray-100 px-[3vh] py-[1.85vh]"
        >
          <DMDiv className="flex min-w-0 items-center gap-[1.6vh]">
            <SkeletonSoftBlock className="h-[4vh] w-[4vh]" />
            <DMDiv>
              <SkeletonBlock className="h-[1.75vh] w-[11vh]" />
              <SkeletonSoftBlock className="mt-[0.75vh] h-[1.75vh] w-[58vh] max-w-[52vw]" />
            </DMDiv>
          </DMDiv>
          <SkeletonSoftBlock className="h-[1.75vh] w-[7.5vh] rounded-full" />
        </DMDiv>
      ))}
    </DMDiv>

    <DMDiv className="flex h-[4.3vh] w-full items-center justify-center bg-indigo-50/60">
      <SkeletonBlock className="h-[1.55vh] w-[14.5vh]" />
    </DMDiv>
  </DMDiv>
);

const SeoAnalysisSkeleton = () => (
  <DMDiv className="animate-pulse" aria-live="polite" aria-busy="true">
    <DMText className="mb-[1.7vh] text-center text-[2.65vh] font-extrabold leading-none text-gray-950">
      Analysing SEO Optimization...
    </DMText>

    <DMDiv
      as="section"
      className="grid h-[37.8vh] shrink-0 gap-[2.7vh] rounded-[1.5vh] border border-gray-100 bg-gray-100/80 p-[0.9vh] shadow-[0_1px_10px_rgba(15,23,42,0.04)] lg:grid-cols-[1fr_1.12fr_1fr]"
      aria-label="Loading SEO health metrics"
    >
      <DMDiv className="grid grid-rows-5 gap-[0.8vh]">
        {[0, 1, 2, 3, 4].map(item => (
          <SeoMetricSkeletonCard key={item} />
        ))}
      </DMDiv>

      <SeoScoreSkeletonPanel />

      <DMDiv className="grid grid-rows-4 gap-[0.8vh]">
        {[0, 1, 2, 3].map(item => (
          <SeoMetricSkeletonCard key={item} />
        ))}
      </DMDiv>
    </DMDiv>

    <OptimizationPlanSkeleton />
  </DMDiv>
);

const SeoGauge = ({ score }: { score: number }) => (
  <DMDiv
    className="relative mt-[0.5vh] h-[12.6vh] w-[20.8vh]"
    role="img"
    aria-label={`SEO score ${score} percent`}
  >
    <SeoGaugeAsset
      className="h-full w-full overflow-visible"
      style={{ '--score': score } as React.CSSProperties}
      aria-hidden="true"
    />
    <DMDiv className="absolute inset-0 flex items-center justify-center pt-[2.4vh] text-gray-950">
      <DMText as="span" className="flex items-baseline">
        <DMText as="span" className="text-[4.15vh] font-extrabold leading-none">
          {score}
        </DMText>
        <DMText as="span" className="ml-[0.28vh] text-[1.65vh] font-bold">
          %
        </DMText>
      </DMText>
    </DMDiv>
  </DMDiv>
);

const parsePercentage = (value?: string | number) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }

  const parsed = Number(String(value || '0').replace('%', ''));
  return Number.isFinite(parsed) ? parsed : 0;
};

const getMetricStatus = (value?: string | number): SeoMetricStatus => {
  const percentage = parsePercentage(value);

  if (percentage >= 80) {
    return 'Excellent';
  }

  if (percentage >= 40) {
    return 'Good';
  }

  return 'Poor';
};

const formatPercentage = (value?: string | number) =>
  `${parsePercentage(value).toFixed(
    parsePercentage(value) % 1 === 0 ? 0 : 1
  )}%`;

const formatDate = (value?: string) => {
  if (!value) {
    return '--/--/----';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '--/--/----';
  }

  return date.toLocaleDateString('en-GB');
};

const formatWebsiteUrl = (value?: string) => {
  if (!value) {
    return 'Https://';
  }

  return value.startsWith('http') ? value : `https://${value}`;
};

const normalizePriority = (priority?: string): SeoTaskPriority => {
  if (priority === 'LOW') {
    return 'Low Priority';
  }

  if (priority === 'MEDIUM') {
    return 'Medium';
  }

  return 'High Priority';
};

const getPriorityStyles = (priority?: string) => {
  if (priority === 'LOW') {
    return {
      icon: FileImage,
      iconClassName: 'text-emerald-600',
      iconBgClassName: 'bg-emerald-50',
    };
  }

  if (priority === 'MEDIUM') {
    return {
      icon: Type,
      iconClassName: 'text-amber-600',
      iconBgClassName: 'bg-amber-50',
    };
  }

  return {
    icon: AlertCircle,
    iconClassName: 'text-rose-600',
    iconBgClassName: 'bg-rose-50',
  };
};

const buildMetrics = (
  keys: ReadonlyArray<keyof typeof metricMeta>,
  sectionPercentages?: Record<string, string | number>
) =>
  keys.map(key => {
    const value = sectionPercentages?.[key];

    return {
      ...metricMeta[key],
      value: formatPercentage(value),
      status: getMetricStatus(value),
    };
  });

const buildOptimizationTasks = (items: SeoOptimizationItem[] = []) =>
  items.slice(0, 3).map(item => ({
    title: item.issue || item.category || 'SEO recommendation',
    description:
      item.action ||
      item.impact ||
      item.effort ||
      'Review this recommendation to improve SEO health.',
    priority: normalizePriority(item.priority),
    ...getPriorityStyles(item.priority),
  }));

const buildIssueCounts = (
  report?: SeoReport
): Partial<Record<SeoPriority, number>> => {
  if (report?.issues_by_priority) {
    return report.issues_by_priority;
  }

  return (report?.optimization_plan || []).reduce<
    Partial<Record<SeoPriority, number>>
  >((counts, item) => {
    const priority = String(item.priority || '').toUpperCase() as SeoPriority;

    if (
      priority === 'LOW' ||
      priority === 'MEDIUM' ||
      priority === 'HIGH' ||
      priority === 'CRITICAL'
    ) {
      counts[priority] = (counts[priority] || 0) + 1;
    }

    return counts;
  }, {});
};

const SeoScorePanel = ({
  isEmpty,
  date,
  websiteUrl,
  score,
  totalIssues,
  issueCounts,
  isSubmitting,
  onAnalyse,
}: {
  isEmpty: boolean;
  date?: string;
  websiteUrl?: string;
  score: number;
  totalIssues: number;
  issueCounts: Partial<Record<SeoPriority, number>>;
  isSubmitting: boolean;
  onAnalyse: () => void;
}) => (
  <DMDiv
    as="section"
    className="flex h-full min-h-0 flex-col items-center rounded-[0.2vh] border border-gray-200 bg-white px-[2.4vh] py-[1.08vh] text-center shadow-[0_1px_6px_rgba(15,23,42,0.05)]"
    aria-labelledby="seo-score-title"
  >
    <DMText className="text-[1.1vh] italic leading-none text-gray-500">
      Generated On : {formatDate(date)}
    </DMText>
    <DMAnchor
      href={formatWebsiteUrl(websiteUrl)}
      target="_blank"
      rel="noreferrer"
      className="mt-[0.55vh] text-[1.08vh] font-medium leading-none text-indigo-500 underline underline-offset-2"
    >
      {formatWebsiteUrl(websiteUrl)}
    </DMAnchor>

    <SeoGauge score={score} />

    <DMText
      as="h2"
      id="seo-score-title"
      className="mt-[0.15vh] text-[1.86vh] font-semibold leading-none text-gray-950"
    >
      Need Improvement
    </DMText>
    <DMButton
      type="button"
      onClick={onAnalyse}
      disabled={isSubmitting}
      className="mt-[1vh] inline-flex h-[4.05vh] min-w-[19vh] items-center justify-center gap-[0.8vh] rounded-[0.5vh] bg-indigo-600 px-[2.5vh] text-[1.55vh] font-semibold text-white transition hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
    >
      {!isEmpty && (
        <RotateCw className="h-[1.6vh] w-[1.6vh]" aria-hidden="true" />
      )}
      {isSubmitting ? 'Analysing...' : isEmpty ? 'Analyse' : 'Re-Analyse'}
    </DMButton>

    <DMText className="mt-[0.95vh] text-[1.9vh] font-extrabold leading-none text-gray-950">
      {totalIssues}
    </DMText>
    <DMText className="mt-[0.45vh] text-[0.98vh] font-semibold uppercase tracking-[0.14em] text-gray-400">
      Issues Found
    </DMText>
    <DMDiv className="mt-[0.6vh] flex items-center justify-center gap-[2.35vh]">
      <IssueSummaryChip
        count={issueCounts.LOW || 0}
        label="Low"
        className="bg-emerald-100 text-emerald-700"
      />
      <IssueSummaryChip
        count={issueCounts.MEDIUM || 0}
        label="Medium"
        className="bg-yellow-100 text-yellow-700"
      />
      <IssueSummaryChip
        count={(issueCounts.HIGH || 0) + (issueCounts.CRITICAL || 0)}
        label="High"
        className="bg-rose-100 text-rose-700"
      />
    </DMDiv>
  </DMDiv>
);

type SeoMetricItem = ReturnType<typeof buildMetrics>[number];

const SeoEmptyMetricCard = ({
  label,
  icon: Icon,
  iconClassName,
  iconBgClassName,
}: SeoMetricItem) => (
  <DMDiv
    as="article"
    className="flex h-full min-h-0 items-center justify-between rounded-[0.7vh] bg-white px-[1.15vh] py-[0.75vh] shadow-[0_1px_8px_rgba(15,23,42,0.06)]"
  >
    <DMDiv className="flex min-w-0 items-center gap-[1.05vh]">
      <DMText
        as="span"
        className={`flex h-[3.6vh] w-[3.6vh] shrink-0 items-center justify-center rounded-[0.7vh] ${iconBgClassName}`}
        aria-hidden="true"
      >
        <Icon
          className={`h-[2.1vh] w-[2.1vh] ${iconClassName}`}
          strokeWidth={2.1}
        />
      </DMText>
      <DMText
        as="span"
        className="truncate text-[1.75vh] font-medium leading-none text-gray-950"
      >
        {label}
      </DMText>
    </DMDiv>

    <DMDiv className="flex h-full items-center">
      <DMText
        as="span"
        className="pr-[1vh] text-[2.65vh] font-extrabold leading-none text-rose-600"
        aria-label="Needs analysis"
      >
        !
      </DMText>
    </DMDiv>
  </DMDiv>
);

const OptimizationPlan = ({
  isEmpty,
  totalIssues,
  tasks,
  onViewAll,
}: {
  isEmpty: boolean;
  totalIssues: number;
  tasks: ReturnType<typeof buildOptimizationTasks>;
  onViewAll: () => void;
}) => (
  <DMDiv
    as="section"
    className="mt-[2.4vh] shrink-0 overflow-hidden rounded-[1.5vh] border border-gray-100 bg-white shadow-[0_1px_10px_rgba(15,23,42,0.06)]"
    aria-labelledby="optimization-plan-title"
  >
    <DMDiv className="px-[3vh] py-[1.8vh]">
      <DMText
        as="h2"
        id="optimization-plan-title"
        className="text-[2.9vh] font-extrabold leading-none text-gray-950"
      >
        {isEmpty
          ? 'Optimization Plan — NO changes found !'
          : `Optimization Plan — ${totalIssues} Changes Required`}
      </DMText>
      <DMText className="mt-[1vh] text-[1.95vh] font-medium leading-none text-gray-600">
        Prioritized checklist to boost your SEO health score
      </DMText>
    </DMDiv>

    {isEmpty ? (
      <DMDiv className="flex h-[22.8vh] flex-col items-center justify-center text-center">
        <DMText className="text-[1.9vh] font-medium leading-none text-gray-950">
          no data found
        </DMText>
        <DMText className="mt-[1vh] text-[1.75vh] font-medium leading-none text-gray-500">
          Prioritized checklist to boost your SEO health score
        </DMText>
      </DMDiv>
    ) : (
      <DMDiv as="ul" aria-label="Optimization recommendations">
        {tasks.map(task => (
          <OptimizationTask key={task.title} {...task} />
        ))}
      </DMDiv>
    )}

    <DMButton
      type="button"
      onClick={onViewAll}
      className={`flex h-[4.3vh] w-full items-center justify-center text-[1.68vh] font-bold transition focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 ${
        isEmpty
          ? 'bg-gray-300 text-slate-700 hover:bg-gray-400'
          : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100'
      }`}
    >
      View All Recommendations
    </DMButton>
  </DMDiv>
);

export default function SEOContent() {
  const navigate = useNavigate();
  const { authData, seoData, isLoading, isAnalysing, analyseSeo } =
    useSeoHealth();

  const report = seoData?.seo_report;
  const sectionPercentages = report?.section_percentages;
  const leftMetrics = React.useMemo(
    () => buildMetrics(leftMetricKeys, sectionPercentages),
    [sectionPercentages]
  );
  const rightMetrics = React.useMemo(
    () => buildMetrics(rightMetricKeys, sectionPercentages),
    [sectionPercentages]
  );
  const optimizationTasks = React.useMemo(
    () => buildOptimizationTasks(report?.optimization_plan),
    [report?.optimization_plan]
  );
  const hasSeoData = Boolean(report);
  const score = report?.overall_seo_percentage || 0;
  const totalIssues = report?.total_issues || 0;
  const issueCounts = React.useMemo(() => buildIssueCounts(report), [report]);
  const websiteUrl =
    seoData?.website_url || report?.url || authData?.BRANCH_DOMAIN;
  const generatedDate =
    seoData?.date || report?.timestamp || report?.time_stamp;

  React.useEffect(() => {
    if (report?.optimization_plan) {
      sessionStorage.setItem(
        'seoOptimizationPlan',
        JSON.stringify(report.optimization_plan)
      );
    }
  }, [report?.optimization_plan]);

  const handleViewAllRecommendations = () => {
    navigate('/digital-marketing/optimization-table', {
      state: { optimizationPlan: report?.optimization_plan || [] },
    });
  };

  return (
    <DMDiv className="flex flex-col overflow-hidden py-[0.55vh] text-gray-950">
      <DMDiv as="header" className="mb-[1.9vh] shrink-0">
        <DMButton
          type="button"
          onClick={() => navigate('/digital-marketing/dashboard')}
          className="inline-flex items-center gap-[0.5vh] text-[1.8vh] font-semibold leading-none text-indigo-600 transition hover:text-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
        >
          <ArrowLeft className="h-[1.9vh] w-[1.9vh]" aria-hidden="true" />
          Back to Dashboard
        </DMButton>
        <DMText
          as="h1"
          className="mt-[0.85vh] text-[3.85vh] font-extrabold leading-none text-gray-950"
        >
          SEO Health
        </DMText>
        <DMText className="mt-[0.6vh] text-[2.05vh] font-medium leading-none text-gray-600">
          Monitor your SEO performance and uncover opportunities to grow your
          rankings.
        </DMText>
      </DMDiv>

      {isLoading || isAnalysing ? (
        <SeoAnalysisSkeleton />
      ) : (
        <>
          <DMDiv
            as="section"
            className="grid h-[37.8vh] shrink-0 gap-[2.7vh] rounded-[1.5vh] border border-gray-100 bg-gray-100 p-[0.9vh] shadow-[0_1px_10px_rgba(15,23,42,0.06)] lg:grid-cols-[1fr_1.12fr_1fr]"
            aria-label="SEO health metrics"
          >
            <DMDiv className="grid grid-rows-4 gap-[0.8vh]">
              {leftMetrics.map(metric =>
                hasSeoData ? (
                  <SeoMetricCard key={metric.label} {...metric} />
                ) : (
                  <SeoEmptyMetricCard key={metric.label} {...metric} />
                )
              )}
            </DMDiv>

            <SeoScorePanel
              isEmpty={!hasSeoData}
              date={generatedDate}
              websiteUrl={websiteUrl}
              score={score}
              totalIssues={totalIssues}
              issueCounts={issueCounts}
              isSubmitting={isAnalysing}
              onAnalyse={() => analyseSeo(websiteUrl)}
            />

            <DMDiv className="grid grid-rows-5 gap-[0.8vh]">
              {rightMetrics.map(metric =>
                hasSeoData ? (
                  <SeoMetricCard key={metric.label} {...metric} />
                ) : (
                  <SeoEmptyMetricCard key={metric.label} {...metric} />
                )
              )}
            </DMDiv>
          </DMDiv>

          <OptimizationPlan
            isEmpty={!hasSeoData}
            totalIssues={totalIssues}
            tasks={optimizationTasks}
            onViewAll={handleViewAllRecommendations}
          />
        </>
      )}
    </DMDiv>
  );
}
